import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _kebabCase from 'lodash/kebabCase';
import { getWalletPixallMetadata } from 'reaxl-analytics';
import { Google, Pixall } from '../analytics/index.js';
import { getInventoryOwnerById, isVDP } from '../utilities/index.js';
import { inventoryTransformer } from '../transformers/pixall/index.js';
import { inventoryTransformer as googleInventoryTransformer } from '../transformers/google/index.js';
import { valueEventCode } from '../constants/index.js';
import { getConsumerMetadata } from '../transformers/pixall/DataLayerTransformer';
const isContinueOnlineCTA = buttonText => buttonText.toLowerCase() === 'continue online';

/**
 * @summary Sends Pixall/Google Click event when CTAs on the NDS calculator are clicked
 * @data cmp, listingType, listingId
 * @events event1
 */

export async function ndsCalcCtaClick(_ref, option) {
  let {
    data = {},
    dataLayer,
    state
  } = _ref;
  if (option === void 0) {
    option = {};
  }
  // eslint-disable-next-line camelcase
  const {
    listingId,
    cmp = 'vw_dlr_fin_opts',
    pageType,
    co_txt_url,
    eventResult,
    ...rest
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const pageId = _get(dataLayer, 'page.brandPg') || _get(dataLayer, 'page.pg', '');
  const zip = dataLayer?.user?.zipCode || '';
  const ownerId = _get(inventory, 'owner.id') || data.ownerId || '';
  const payload = {
    cmp,
    par: 'v_lstg',
    zip,
    // eslint-disable-next-line camelcase
    co_txt_url,
    ...rest
  };
  Pixall.sendClickEvent({
    eventSource: 'calculator',
    eventType: 'view',
    feature: 'dr3',
    label: `${cmp} - ${pageId}`,
    listingCode: ownerId,
    pageType,
    inventory,
    pixallData: {
      clickTypeToListingType: isVDP(dataLayer)
    }
  }, dataLayer, option);
  if (eventResult) {
    Google.sendClickEvent({
      eventElement: 'nds',
      eventResult
    }, option);
  }

  // return data for custom event
  return payload;
}

/**
 * @summary Sends Pixall/Google Click event when Pencilling on the NDS calculator
 * @data cmp, listingType, listingId
 * @events event1
 */
export function ndsPencilingEvents(_ref2, option) {
  let {
    data = {},
    dataLayer,
    event,
    state
  } = _ref2;
  if (option === void 0) {
    option = {};
  }
  const {
    brand = ''
  } = state;
  const {
    customBrand
  } = option;
  const {
    listingId,
    cmp,
    feature,
    eventName,
    eventResult,
    walletData = {},
    ...rest
  } = data;
  const {
    paymentsInfo,
    userModified,
    price,
    offerPrice
  } = walletData;
  const {
    dealMetadata,
    tradeinMetadata
  } = getWalletPixallMetadata(paymentsInfo, userModified, price, offerPrice);
  const inventory = getInventoryOwnerById(state, listingId);
  const zip = dataLayer?.user?.zipCode || '';
  const linkCopy = event?.target?.innerText || event?.target?.textContent;
  const ownerId = _get(inventory, 'owner.id') || data.ownerId || '';
  const isPrivateSeller = inventory?.owner?.privateSeller;
  let label = cmp;

  // Handle the label of the taxes & fees cta on the unified dealstarter
  if (customBrand === 'myWallet' && ['tf_see_dtls', 'see_dtls'].includes(label)) {
    label = 'g_tax_fee';
  }
  const payload = {
    cmp: label,
    par: 'v_lstg',
    zip,
    co_txt_url: linkCopy,
    ...rest
  };
  if (brand === 'kbb') {
    payload.linkName = linkCopy;
  }
  Pixall.sendClickEvent({
    calculatorType: 'payment',
    eventSource: 'calculator',
    eventType: 'step',
    label,
    listingCode: ownerId,
    feature,
    inventory,
    ...(!isPrivateSeller && {
      pixallData: {
        clickTypeToListingType: true
      }
    }),
    ...(isPrivateSeller && {
      feature: 'psx',
      pageId: 'fyc_psx_vdp',
      listingType: 'pvt'
    }),
    tradeinMetadata,
    dealMetadata
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'nds',
    eventAction: 'activity',
    eventResult,
    eventData: {
      vehicle: googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      })
    },
    ...(eventName && {
      eventName
    })
  }, option);
  // return data for custom event
  return payload;
}

/**
 * @summary Sends Pixall/Google Click event only onces when DropDowns or Inputs on the NDS calculator are penciled
 * @data listingId, feature
 * @events event1
 */
export function ndsCalcInteraction(_ref3, option) {
  let {
    data = {},
    dataLayer,
    state
  } = _ref3;
  if (option === void 0) {
    option = {};
  }
  const {
    listingId,
    feature,
    consumerMetadata,
    walletData = {},
    ...rest
  } = data;
  const {
    paymentsInfo,
    userModified,
    price,
    offerPrice
  } = walletData;
  const {
    dealMetadata,
    tradeinMetadata
  } = getWalletPixallMetadata(paymentsInfo, userModified, price, offerPrice);
  const inventory = getInventoryOwnerById(state, listingId);
  const zip = dataLayer?.user?.zipCode || '';
  const ownerId = _get(inventory, 'owner.id') || data.ownerId || '';
  const isPrivateSeller = inventory?.owner?.privateSeller;
  const payload = {
    cmp: 'nds_true',
    par: 'v_lstg',
    zip,
    ...rest
  };
  Pixall.sendClickEvent({
    calculatorType: 'payment',
    eventSource: 'calculator',
    eventType: 'step',
    label: 'nds_pencil',
    listingCode: ownerId,
    feature,
    inventory,
    ...(!isPrivateSeller && {
      pixallData: {
        clickTypeToListingType: true
      }
    }),
    ...(isPrivateSeller && {
      feature: 'psx',
      pageId: 'fyc_psx_vdp',
      listingType: 'pvt'
    }),
    consumerMetadata,
    tradeinMetadata,
    dealMetadata,
    cdl: {
      eventProperties: {
        event: {
          valueEventCode: 'F638'
        }
      }
    }
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'nds',
    eventResult: 'pencil::nds',
    eventData: {
      vehicle: {
        productConfiguration: 'nds',
        dealershipId: ownerId?.toString()
      }
    }
  }, option);

  // return data for custom event
  return payload;
}

/**
 * @summary Sends Pixall/Google Click event when dealer financing CTA are clicked
 * @data cmp, listingType, listingId
 * @events event1,event23
 */
export async function ndsDealerFinancingCTA(_ref4, option) {
  let {
    data = {},
    dataLayer,
    state
  } = _ref4;
  if (option === void 0) {
    option = {};
  }
  const {
    listingId,
    cmp,
    ...rest
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const pageId = _get(dataLayer, 'page.brandPg') || _get(dataLayer, 'page.pg', '');
  const zip = dataLayer?.user?.zipCode || '';
  const ownerId = _get(inventory, 'owner.id') || data.ownerId || '';
  const payload = {
    cmp,
    par: 'v_lstg',
    zip,
    ...rest
  };
  Pixall.sendClickEvent({
    eventSource: 'dealership',
    feature: 'nds',
    label: `Click to Dealer Site - ${cmp} - ${pageId}`,
    listingCode: ownerId,
    inventory,
    pixallData: {
      clickTypeToListingType: isVDP(dataLayer)
    },
    cdl: {
      eventProperties: {
        event: {
          valueEventCode: '0AAD'
        }
      }
    }
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'dealer',
    eventResult: 'sitelink::nds',
    eventData: {
      vehicle: {
        dealershipId: ownerId?.toString()
      }
    },
    vehicle: {
      cj_ga_event_value_code: '0AAD' // CD166
    }
  }, option);

  // return data for custom event
  return payload;
}

/**
 * @summary Sends Google and Pixall Impressions event on NDS in view impression
 * @data listingId
 */
export function ndsInviewImpression(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  if (option === void 0) {
    option = {};
  }
  const {
    data = {},
    dataLayer = {},
    state,
    history
  } = metadata;
  const {
    listingId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const isPrivateSeller = inventory?.owner?.privateSeller;
  Google.sendImpressions(metadata, {
    eventElement: 'dr',
    eventResult: 'inview::nds',
    eventData: {
      vehicle: googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      })
    }
  }, option);
  const vehicleObject = inventoryTransformer.getCDLVehicleTransformer({
    ...googleInventoryTransformer(inventory, {
      target: 'vehicle',
      dataLayer
    })
  });
  const pageId = _get(dataLayer, 'page.brandPg') || _get(dataLayer, 'page.pg', '');
  Pixall.sendImpression({
    dataLayer,
    history
  }, {
    calculatorType: 'payment',
    eventSource: 'dealership',
    eventType: 'impression',
    label: 'inview::nds',
    pageId,
    vehicleType: inventory?.listingType || '',
    clickType: inventory?.clickType,
    feature: inventory?.paymentServices?.digitalRetailingType === 'accelerate' ? 'nds_calculator' : 'nds',
    ...inventoryTransformer.getInventoryData(inventory),
    cdl: {
      eventProperties: {
        vehicle: {
          ...vehicleObject
        }
      }
    },
    ...(isPrivateSeller && {
      eventSource: 'calculator',
      feature: 'psx',
      listingType: 'pvt',
      pageId: 'fyc_psx_vdp'
    })
  }, option);
  return {};
}

/**
 * @summary Sends Google Impressions event on InsuranceSponsor in view impression
 */
export function ndsInsuranceSponsorInviewImpression(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  if (option === void 0) {
    option = {};
  }
  const {
    data = {},
    dataLayer = {},
    state,
    history
  } = metadata;
  const {
    listingId,
    sponsor,
    ownerId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  Pixall.sendImpression({
    dataLayer,
    history
  }, {
    eventSource: 'insurance',
    feature: 'nds',
    label: `insurance_${sponsor.toLowerCase()}-insurance_nds`,
    clickType: inventory.clickType,
    listingCode: ownerId,
    ...inventoryTransformer.getInventoryData(inventory)
  }, option);
  Google.sendImpressions(metadata, {
    eventElement: 'partner',
    eventResult: `insurance::${sponsor.toLowerCase()}-insurance::get-a-quote-for-this-vehicle-in-under-5-minutes::get-a-quote`,
    eventData: {
      vehicle: googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      })
    }
  }, option);
  return {};
}

/**
 * @summary Sends Google Click event event on InsuranceSponsor CTA click
 */
export function ndsInsuranceSponsorCtaClick(_ref5, option) {
  let {
    data = {},
    dataLayer,
    state
  } = _ref5;
  if (option === void 0) {
    option = {};
  }
  const {
    listingId,
    sponsor,
    ownerId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const sponsorName = sponsor?.toLowerCase();
  Pixall.sendClickEvent({
    eventSource: 'insurance',
    feature: 'nds',
    label: `cta_insurance_${sponsorName}-insurance_get-a-quote_nds`,
    listingCode: ownerId,
    pixallData: {
      clickTypeToListingType: true
    },
    inventory,
    vendor: `${sponsorName}-insurance`
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'partner',
    eventResult: `cta::insurance::${sponsorName}-insurance::nds::get-a-quote`,
    eventData: {
      vehicle: googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      })
    }
  }, option);
  return {};
}

/**
 * @summary Sends Google and Pixall Impressions event on NDS eligible impression
 * @data listingId
 */
export function ndsEligibleImpression(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  if (option === void 0) {
    option = {};
  }
  const {
    data = {},
    state,
    dataLayer = {},
    history
  } = metadata;
  const inventory = getInventoryOwnerById(state, data.listingId);
  const isPrivateSeller = inventory?.owner?.privateSeller;
  const ownerId = _get(inventory, 'owner.id') || data.ownerId || '';
  const vehicleObject = inventoryTransformer.getCDLVehicleTransformer({
    ...googleInventoryTransformer(inventory, {
      target: 'vehicle',
      dataLayer
    })
  });
  const pageId = _get(dataLayer, 'page.brandPg') || _get(dataLayer, 'page.pg', '');
  Pixall.sendImpression({
    dataLayer,
    history
  }, {
    calculatorType: 'payment',
    eventSource: 'dealership',
    eventType: 'impression',
    label: 'eligible::nds',
    feature: inventory?.paymentServices?.digitalRetailingType === 'accelerate' ? 'nds_calculator' : 'nds',
    pageId,
    pageType: 'vdp',
    listingCode: ownerId,
    clickType: inventory.clickType,
    ...inventoryTransformer.getInventoryData(inventory),
    cdl: {
      eventProperties: {
        vehicle: {
          ...vehicleObject
        }
      }
    },
    ...(isPrivateSeller && {
      eventSource: 'calculator',
      feature: 'psx',
      listingType: 'pvt',
      pageId: 'fyc_psx_vdp'
    })
  }, option);
  Google.sendImpressions(metadata, {
    eventElement: 'dr',
    eventResult: 'eligible::nds',
    eventData: {
      vehicle: {
        dealershipId: ownerId.toString()
      }
    }
  }, option);
  return {};
}

/**
 * @summary Sends Google Click event when tabs are changed
 * @events event1
 */
export function ndsNavigationClick(_ref6, option) {
  let {
    data = {},
    dataLayer,
    state
  } = _ref6;
  if (option === void 0) {
    option = {};
  }
  const {
    eventResult,
    cmp,
    listingId,
    walletData = {}
  } = data;
  const {
    paymentsInfo,
    userModified,
    price,
    offerPrice
  } = walletData;
  const {
    dealMetadata,
    tradeinMetadata
  } = getWalletPixallMetadata(paymentsInfo, userModified, price, offerPrice);
  const pageId = _get(dataLayer, 'page.brandPg') || _get(dataLayer, 'page.pg', '');
  const inventory = getInventoryOwnerById(state, listingId);
  const ownerId = _get(inventory, 'owner.id') || data.ownerId || '';
  const isPrivateSeller = inventory?.owner?.privateSeller;
  Google.sendClickEvent({
    eventElement: 'nds',
    eventAction: 'activity',
    eventResult,
    eventData: {
      vehicle: googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      })
    }
  }, option);
  Pixall.sendClickEvent({
    calculatorType: 'payment',
    eventSource: 'calculator',
    eventType: 'click',
    label: cmp,
    listingCode: ownerId,
    pageId,
    feature: inventory?.paymentServices?.digitalRetailingType === 'accelerate' ? 'dr3' : 'mywallet',
    inventory,
    ...(!isPrivateSeller && {
      pixallData: {
        clickTypeToListingType: true
      }
    }),
    ...(isPrivateSeller && {
      feature: 'psx',
      pageId: 'fyc_psx_vdp',
      listingType: 'pvt'
    }),
    tradeinMetadata,
    dealMetadata
  }, dataLayer, option);
  return {
    cmp: eventResult
  };
}
export function ndsLendingOptionsCTAClick(_ref7, option) {
  let {
    data = {},
    dataLayer,
    event,
    state
  } = _ref7;
  if (option === void 0) {
    option = {};
  }
  const {
    listingId,
    enableCreditApp,
    isWestlakeEnabled = false,
    placement = 'nds',
    uiContext,
    vendor
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const isPrivateSeller = inventory?.owner?.privateSeller;
  const {
    innerText,
    textContent,
    parentNode: {
      innerText: parentNodeInnerText
    } = {}
  } = event?.target || {};
  const linkCopy = (innerText || textContent || parentNodeInnerText || '').trim();
  const isAccelerate = _get(inventory, 'paymentServices.digitalRetailingType', '') === 'accelerate';
  const isSaCa = enableCreditApp && !isAccelerate;
  const isExpFin = linkCopy === 'Explore Financing';
  const isApplyForFinancing = linkCopy === 'Apply for Financing';
  let partnerPrefix = isApplyForFinancing ? 'caca-' : '';
  let partner;
  let label = isExpFin ? 'exp_fin' : 'get_prequal';
  let feature = 'caca';
  let eventElement = 'nds';
  let eventSource = 'dealership';
  let applicationType;
  let calculatorType;
  let hasListingType;
  let leadType;
  let customValueEventCode;
  let gaVehicle;

  // precedence order: accelerate > saca > westlake
  if (isAccelerate) {
    partner = 'amd'; // caca should be only for accelerate or amd dealers.
    feature = 'dr3';
    eventElement = 'accelerate';
    partnerPrefix = 'caca-';
  } else if (isSaCa) {
    partner = 'saca';
  } else if (isWestlakeEnabled) {
    partner = 'westlake';
    customValueEventCode = valueEventCode.WESTLAKE;
  } else if (isExpFin && isPrivateSeller) {
    partner = 'amd';
    feature = 'psx';
    partnerPrefix = 'caca-';
    eventSource = 'link';
  }
  if (partner && (isExpFin && isWestlakeEnabled || !isExpFin)) {
    label += `_${partner}`;
  }
  if (uiContext === 'tile' || uiContext === 'lenderTile') {
    applicationType = 'loan';
    eventSource = 'application';
    label = 'get_loan_expl_fin';
    calculatorType = 'payment';
    hasListingType = true;
  }
  if (isApplyForFinancing) {
    label += `-${placement}`;
    eventSource = 'lead';
    calculatorType = 'payment';
    leadType = 'credit';
    customValueEventCode = valueEventCode.CREDIT_APP;
    gaVehicle = {
      cj_ga_event_value_code: customValueEventCode
    };
  }
  Pixall.sendClickEvent({
    eventSource,
    feature,
    label,
    inventory,
    pixallData: {
      clickTypeToListingType: isVDP(dataLayer)
    },
    applicationType,
    calculatorType,
    vendor,
    hasListingType,
    leadType,
    ...(customValueEventCode && {
      cdl: {
        eventProperties: {
          event: {
            valueEventCode: customValueEventCode
          }
        }
      }
    })
  }, dataLayer, option);
  if (partner) {
    Google.sendClickEvent({
      eventElement,
      eventResult: `text-link::${partnerPrefix}${partner}::${_kebabCase(linkCopy)}::${placement}`,
      eventNonInteractive: false,
      eventData: {
        vehicle: googleInventoryTransformer(inventory, {
          target: 'vehicle',
          dataLayer
        })
      },
      vehicle: gaVehicle
    }, option);
  }
  return {};
}
export function ndsGetPreQualifiedExploreFinancingClick(_ref8, option) {
  let {
    data = {},
    dataLayer,
    event,
    state
  } = _ref8;
  if (option === void 0) {
    option = {};
  }
  const {
    listingId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  Pixall.sendClickEvent({
    eventSource: 'dealership',
    feature: 'caca',
    label: 'get_prequal_explore_financing',
    inventory
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'nds',
    eventResult: 'text-link::caca-saca::get-pre-qualified-explore-financing'
  }, option);
  return {};
}
export function ndsOfferBreakDownClick(_ref9, option) {
  let {
    data = {},
    dataLayer,
    event,
    state
  } = _ref9;
  if (option === void 0) {
    option = {};
  }
  const {
    listingId,
    tabName,
    walletData = {}
  } = data;
  const {
    customBrand = ''
  } = option;
  const inventory = getInventoryOwnerById(state, listingId);
  const isPrivateSeller = inventory?.owner?.privateSeller;
  const isUDS = customBrand === 'myWallet';
  const {
    paymentsInfo,
    price,
    offerPrice
  } = walletData;
  const {
    dealMetadata,
    tradeinMetadata
  } = getWalletPixallMetadata(paymentsInfo, '', price, offerPrice);
  const pixallPayload = {
    ...(!isPrivateSeller && {
      pixallData: {
        clickTypeToListingType: true
      }
    }),
    ...(isPrivateSeller && {
      pageId: 'fyc_psx_vdp',
      listingType: 'pvt'
    }),
    eventSource: isPrivateSeller ? 'application' : 'dealership',
    feature: isPrivateSeller ? 'psx' : 'caca',
    label: `dropdown::offer-breakdown::${tabName}`,
    inventory,
    tradeinMetadata,
    dealMetadata
  };

  // To handle UnifiedDealStarter brand
  if (isUDS) {
    pixallPayload.calculatorType = 'payment';
    pixallPayload.eventSource = 'calculator';
    pixallPayload.eventType = 'step';
    pixallPayload.feature = 'MyWallet';
  }
  Pixall.sendClickEvent(pixallPayload, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'nds',
    eventResult: `dropdown::offer-breakdown::${tabName}`,
    eventData: {
      vehicle: googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      })
    }
  }, option);
  return {};
}
export function ndsKBBTradeInCTAClick(_ref10, option) {
  let {
    data = {},
    dataLayer,
    state
  } = _ref10;
  if (option === void 0) {
    option = {};
  }
  const {
    listingId
  } = data;
  const {
    customBrand = ''
  } = option;
  const inventory = getInventoryOwnerById(state, listingId);
  const ownerId = _get(inventory, 'owner.id') || data.ownerId || '';
  const isUDS = customBrand === 'myWallet';
  const pixallPayload = {
    eventSource: 'calculator',
    eventType: 'click',
    label: 'val_v_kbb',
    listingCode: ownerId,
    inventory,
    pixallData: {
      clickTypeToListingType: isVDP(dataLayer)
    }
  };

  // To handle UnifiedDealStarter brand
  if (isUDS) {
    pixallPayload.label = 'val_v_kbb';
    pixallPayload.calculatorType = 'payment';
    pixallPayload.eventSource = 'calculator';
    pixallPayload.eventType = 'step';
    pixallPayload.feature = 'MyWallet';
  }
  Pixall.sendClickEvent(pixallPayload, dataLayer, option);
  return {};
}
export function ndsEsntialBuyOnlineClick(_ref11, option) {
  let {
    data = {},
    dataLayer,
    event,
    state
  } = _ref11;
  if (option === void 0) {
    option = {};
  }
  const {
    listingId,
    ctaLocation
  } = data;
  let {
    placement = ''
  } = data;
  const {
    brand = ''
  } = state;

  // Backwards compatibility for ctaLocation
  // Please use placement for new implementations
  if (ctaLocation && !placement) {
    placement = ctaLocation;
  }
  const inventory = getInventoryOwnerById(state, listingId);
  const isAccelerate = _get(inventory, 'paymentServices.digitalRetailingType', '') === 'accelerate';
  const isEsntial = !!_get(inventory, 'esntial', false);
  const textContent = (event?.target?.innerText || event?.target?.textContent || '').trim();
  const pageId = _get(dataLayer, 'page.brandPg') || _get(dataLayer, 'page.pg', '');
  let eventSource = brand === 'kbb' ? 'calculator' : 'vehicle';
  let eventElement = 'nds';
  let eventResult = `lead::esntial::${placement}`;
  let cmp = textContent.replace(/\s+/g, '_').toLowerCase();
  let label = eventResult;
  let feature = 'nds';
  let customValueEventCode;
  if (isAccelerate) {
    cmp = 'pur_now_ecomm_dplnk';
    eventElement = 'dealer';
    eventResult = `sitelink::ecomm-deeplink::purchase-now::${placement}`;
    eventSource = 'dealership';
    feature = 'NDS Calculator';
    label = `Click to Dealer Site - ${cmp} - ${pageId}`;
  }
  if (isEsntial) {
    eventElement = 'dealer';
    eventSource = 'dealership';
    feature = 'Drive the 5';
    eventResult = `sitelink::ecomm-${isAccelerate ? 'amd' : 'deeplink'}::start-purchase-now::${placement}`;
    label = `Click to Dealer Site - pur_now_ecomm_${isAccelerate ? 'amd' : 'dplnk'} - ${placement}`;
    customValueEventCode = 'RT42';
    if (isContinueOnlineCTA(textContent)) {
      eventElement = 'nds';
      label = `Click to Dealer Site - lead::esntial::${placement}`;
      eventResult = `lead::esntial::${placement}`;
      customValueEventCode = '0AAD';
    }
  }
  Google.sendClickEvent({
    eventElement,
    eventResult,
    eventNonInteractive: false,
    eventData: {
      vehicle: googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      })
    },
    ...(customValueEventCode && {
      vehicle: {
        cj_ga_event_value_code: customValueEventCode
      }
    })
  }, option);
  if (isContinueOnlineCTA(textContent) || isAccelerate || isEsntial) {
    Pixall.sendClickEvent({
      eventSource,
      feature,
      label,
      inventory,
      pixallData: {
        clickTypeToListingType: isVDP(dataLayer)
      },
      ...(customValueEventCode && {
        cdl: {
          eventProperties: {
            event: {
              valueEventCode: customValueEventCode
            }
          }
        }
      })
    }, dataLayer, option);
  }
  return {
    cmp
  };
}
export function ndsPayoffQuoteCTAClick(_ref12, option) {
  let {
    data = {},
    dataLayer,
    state
  } = _ref12;
  if (option === void 0) {
    option = {};
  }
  const {
    listingId
  } = data;
  const pageId = _get(dataLayer, 'page.brandPg') || _get(dataLayer, 'page.pg', '');
  const inventory = getInventoryOwnerById(state, listingId);
  const ownerId = _get(inventory, 'owner.id') || data.ownerId || '';
  Pixall.sendClickEvent({
    eventSource: 'calculator',
    eventType: 'click',
    label: 'get_payoff_qt',
    listingCode: ownerId,
    pageId,
    inventory,
    pixallData: {
      clickTypeToListingType: isVDP(dataLayer)
    }
  }, dataLayer, option);
  return {};
}
export function ndsVerifyCreditCTAClick(_ref13, option) {
  let {
    data = {},
    dataLayer,
    state
  } = _ref13;
  if (option === void 0) {
    option = {};
  }
  const {
    listingId
  } = data;
  const pageId = _get(dataLayer, 'page.brandPg') || _get(dataLayer, 'page.pg', '');
  const inventory = getInventoryOwnerById(state, listingId);
  const isAccelerate = _get(inventory, 'paymentServices.digitalRetailingType', '') === 'accelerate';
  const ownerId = _get(inventory, 'owner.id') || data.ownerId || '';
  Pixall.sendClickEvent({
    eventSource: 'calculator',
    eventType: 'click',
    feature: isAccelerate ? 'dr3' : 'MyWallet',
    label: 'text-link::verify-my-credit',
    listingCode: ownerId,
    pageId,
    inventory,
    pixallData: {
      clickTypeToListingType: isVDP(dataLayer)
    }
  }, dataLayer, option);
  return {};
}
export async function ndsAutoFill(_ref14, option) {
  let {
    data = {},
    dataLayer,
    state
  } = _ref14;
  if (option === void 0) {
    option = {};
  }
  const {
    listingId,
    walletData = {}
  } = data;
  const {
    paymentsInfo,
    price,
    offerPrice
  } = walletData;
  const consumerMetadata = await getConsumerMetadata();
  if (!_isEmpty(consumerMetadata)) {
    consumerMetadata.walletId = paymentsInfo?.walletId;
  }
  const {
    dealMetadata,
    tradeinMetadata
  } = getWalletPixallMetadata(paymentsInfo, '', price, offerPrice);
  const inventory = getInventoryOwnerById(state, listingId);
  const ownerId = _get(inventory, 'owner.id') || '';
  Pixall.sendClickEvent({
    eventSource: 'calculator',
    eventType: 'autofill',
    calculatorType: 'payment',
    label: 'autofill_pencil',
    feature: 'MyWallet',
    consumerMetadata,
    dealMetadata,
    tradeinMetadata,
    pixallData: {
      clickTypeToListingType: true
    },
    listingCode: ownerId,
    inventory
  }, dataLayer, option);
  return {};
}
export function ndsSelectButtonInteraction(_ref15, option) {
  let {
    data = {},
    dataLayer,
    state
  } = _ref15;
  const {
    cmp
  } = data;
  const inventoryId = dataLayer.vehicle?.id;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const isAccelerate = _get(inventory, 'paymentServices.digitalRetailingType', '') === 'accelerate';
  const isPrivateSeller = inventory?.owner?.privateSeller;
  let eventResult;
  if (cmp === 'edt_pr') {
    eventResult = 'listing-price';
  }
  Pixall.sendClickEvent({
    calculatorType: 'payment',
    eventType: 'step',
    eventSource: 'calculator',
    feature: isAccelerate ? 'dr3' : 'MyWallet',
    inventory,
    label: cmp,
    ...(isPrivateSeller ? {
      feature: 'psx',
      pageId: 'fyc_psx_vdp',
      listingType: 'pvt'
    } : {
      pixallData: {
        clickTypeToListingType: true
      }
    })
  }, dataLayer, option);
  if (eventResult) {
    Google.sendClickEvent({
      eventAction: 'activity',
      eventElement: 'nds',
      eventResult,
      vehicle: googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      })
    }, option);
  }
  return {};
}

/**
 * @summary Sends Pixall Impressions event on NDS calculator start
 * @data listingId, walletData
 */
export async function ndsStartCalculatorImpression(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  if (option === void 0) {
    option = {};
  }
  const {
    data = {},
    dataLayer = {},
    state,
    history
  } = metadata;
  const {
    listingId,
    walletData
  } = data;
  const {
    customBrand = ''
  } = option;
  const isUDS = customBrand === 'myWallet';
  const inventory = getInventoryOwnerById(state, listingId);
  const isAccelerate = _get(inventory, 'paymentServices.digitalRetailingType', '') === 'accelerate';
  const {
    paymentsInfo,
    listingPrice
  } = walletData;
  const {
    dealMetadata,
    tradeinMetadata
  } = getWalletPixallMetadata(paymentsInfo, '', listingPrice);
  let feature;
  // To handle UnifiedDealStarter brand
  if (isUDS) {
    feature = 'can_dealertrack_dr';
  } else {
    feature = isAccelerate ? 'dr3' : 'caca';
  }
  Pixall.sendImpression({
    dataLayer,
    history
  }, {
    calculatorType: 'payment',
    eventSource: 'calculator',
    eventType: 'start',
    label: 'loan',
    clickType: inventory?.clickType,
    feature,
    dealMetadata,
    tradeinMetadata,
    ...inventoryTransformer.getInventoryData(inventory)
  }, option);
  return {};
}
export default {
  ndsAutoFill,
  ndsCalcCtaClick,
  ndsCalcInteraction,
  ndsDealerFinancingCTA,
  ndsPencilingEvents,
  ndsOfferBreakDownClick,
  ndsInviewImpression,
  ndsEligibleImpression,
  ndsNavigationClick,
  ndsLendingOptionsCTAClick,
  ndsEsntialBuyOnlineClick,
  ndsKBBTradeInCTAClick,
  ndsInsuranceSponsorInviewImpression,
  ndsInsuranceSponsorCtaClick,
  ndsPayoffQuoteCTAClick,
  ndsSelectButtonInteraction,
  ndsStartCalculatorImpression
};